import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import config from './amplifyconfiguration.json';
import { createVote } from "./graphql/mutations";
import { initEffects } from "./effects";

Amplify.configure(config);
const client = generateClient();

// ---------------------------------------------------------------------------
// Per-show configuration — this is the only block that changes year to year.
// ---------------------------------------------------------------------------

// Confirmed for 2026: four votes per viewer, matching SBGC 2026. The Cedys ran
// a single-vote ballot in 2024/2025, so this is a deliberate change — the
// cookie names below are year-suffixed, so returning viewers start fresh and
// won't inherit a 2025 single-vote cookie.
const MAX_VOTES = 4;

// Cookies are namespaced per show year so a returning viewer starts fresh.
const VOTE_COOKIE = "cedysvote26";
const COUNT_COOKIE = "cedysvotecount26";

// Broadcast is Friday, September 18 2026 (8/7c) — this sits well past it so a
// viewer's ballot state survives the whole show night and any repeat airings.
const COOKIE_EXPIRES = "Thu, 31 Dec 2026 23:59:59 GMT";

// Voting opens at 8:00pm ET on show night: midnight UTC, since September is
// EDT (UTC-4). Until then "/" shows the promo and a vote click does nothing;
// at this instant the ballot takes over on its own. See applyGate() below.
const OPENS_AT = Date.parse("2026-09-19T00:00:00Z");
const OPENS_MESSAGE = "Voting opens at 8pm ET!";

// Flip to false after the show to freeze the ballot, and name the winner's
// slug (the option's id in index.html and vote.html) so its checkmark is shown.
const VOTING_OPEN = false;
const WINNER = "beatboxer26";
const CLOSED_MESSAGE = "Voting is closed. See you next year!";

// ---------------------------------------------------------------------------

function readCookie(name) {
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return cookie ? cookie.split("=")[1] : null;
}

function writeCookie(name, value) {
  document.cookie = name + "=" + value + "; expires=" + COOKIE_EXPIRES + "; Secure";
}

function getVoteCount() {
  const raw = readCookie(COUNT_COOKIE);
  const count = raw ? parseInt(raw, 10) : 0;
  return Number.isNaN(count) ? 0 : count;
}

function setVoteCount(count) {
  writeCookie(COUNT_COOKIE, count);
}

function getLastVote() {
  return readCookie(VOTE_COOKIE);
}

function setLastVote(votedFor) {
  writeCookie(VOTE_COOKIE, votedFor);
}

// A vote counts as cast on the client the moment the button is pressed, whether
// or not AppSync answers — a viewer who taps once should never be asked twice.
function recordVote(votedFor, currentCount) {
  setVoteCount(currentCount + 1);
  setLastVote(votedFor);
  document.getElementById(votedFor).classList.add("yes");
  document.getElementById("instructions").innerText = "Thanks for voting!";
}

// ---------------------------------------------------------------------------
// Showtime switch. "/" carries both the promo and the ballot, and this decides
// which one is up and whether a click can record a vote — so nothing has to be
// deployed or changed in the console at 8pm.
// ---------------------------------------------------------------------------

// The gate runs on AppSync's clock rather than the viewer's. The site's own Date
// header is useless for this: CloudFront caches the HTML (s-maxage is a year)
// and freezes Date at cache-fill time. AppSync responses are never cached and
// expose Date to the browser, and `{ __typename }` touches no data source.
let clockOffset = 0;
let clockSettled = false;
let clockReady = Promise.resolve();
let currentState = null;

async function syncClock() {
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), 5000);
  try {
    const sent = Date.now();
    const res = await fetch(config.aws_appsync_graphqlEndpoint, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-api-key": config.aws_appsync_apiKey,
      },
      body: JSON.stringify({ query: "{ __typename }" }),
      signal: abort.signal,
    });
    const received = Date.now();
    const serverTime = Date.parse(res.headers.get("Date"));
    if (!Number.isNaN(serverTime)) {
      // Date is truncated to the whole second, so take the middle of it, and
      // assume it was stamped halfway through the round trip.
      clockOffset = serverTime + 500 - (sent + received) / 2;
    }
  } catch (err) {
    // Fall back to the device clock rather than holding the ballot shut.
    console.error(err);
  } finally {
    clearTimeout(timeout);
    clockSettled = true;
  }
}

function votingHasOpened() {
  return Date.now() + clockOffset >= OPENS_AT;
}

// Only "/" has a promo block. /vote keeps its ballot up throughout so it can be
// reviewed before the show.
function setPromoVisible(visible) {
  const promo = document.getElementById("promo");
  if (!promo) return;
  promo.hidden = !visible;
  document.getElementById("ballot").hidden = visible;
  // Over the key art the Instagram CTA sits bare; on the ballot it gets the
  // orange panel and CLICK prompt, matching /vote.
  const follow = document.getElementById("follow");
  follow.classList.toggle("plain", visible);
  follow.querySelectorAll(".click").forEach((el) => (el.hidden = visible));
}

// Puts the page in whichever state the clock and config call for, touching the
// DOM only when that state changes. Returns the state.
function applyGate() {
  const state = !VOTING_OPEN ? "closed" : votingHasOpened() ? "open" : "waiting";
  if (state === currentState) return state;
  currentState = state;

  const options = document.getElementById("options");
  const instructions = document.getElementById("instructions");
  options.classList.remove("novote", "done");
  options.querySelectorAll(".yes").forEach((el) => el.classList.remove("yes"));

  if (state === "waiting") {
    setPromoVisible(true);
    options.classList.add("novote");
    instructions.innerText = OPENS_MESSAGE;
  } else if (state === "closed") {
    setPromoVisible(false);
    options.classList.add("done");
    if (WINNER) {
      document.getElementById(WINNER).classList.add("yes");
    }
    instructions.innerText = CLOSED_MESSAGE;
  } else if (getVoteCount() >= MAX_VOTES) {
    setPromoVisible(false);
    const lastVote = getLastVote();
    options.classList.add("done");
    if (lastVote && document.getElementById(lastVote)) {
      document.getElementById(lastVote).classList.add("yes");
    }
    instructions.innerText = "Thanks for voting!";
  } else {
    setPromoVisible(false);
    options.classList.add("novote");
    instructions.innerText = "Vote for your favorite!";
  }
  return state;
}

async function sendVote(e) {
  const votedFor = e.target.dataset.ad;

  // A click that lands before the clock sync finishes waits for it, rather than
  // being judged on the device clock.
  await clockReady;
  const currentCount = getVoteCount();

  if (!VOTING_OPEN || currentCount >= MAX_VOTES) {
    return;
  }

  if (applyGate() !== "open") {
    return;
  }

  document.getElementById("options").classList.remove("novote");
  document.getElementById("options").classList.add("done");
  document.getElementById("instructions").innerText = "Processing your vote...";

  // Guarded: an ad blocker can leave gtag undefined, and a throw here would
  // otherwise skip the createVote call entirely and lose the vote.
  if (typeof gtag === "function") gtag('event', votedFor);

  try {
    const newVote = await client.graphql({
      query: createVote,
      variables: {
        input: {
          vote: votedFor,
        }
      }
    });

    console.log(newVote);
  } catch (err) {
    console.error(err);
  }

  recordVote(votedFor, currentCount);
}

function init() {
  console.log("run init");

  initEffects();

  document.getElementById("termsLink").onclick = function () {
    document.getElementById("terms").classList.toggle("show");
  };

  document.querySelectorAll("[data-promo]").forEach((el) => {
    el.addEventListener("click", () => {
      if (typeof gtag === "function") gtag("event", "promo_" + el.dataset.promo);
    });
  });

  document.querySelectorAll(".vote").forEach((item) => {
    item.addEventListener("click", sendVote);
  });

  // First pass on the device clock, so an accurate one — the common case —
  // never flashes the wrong page. The sync then corrects it against AppSync.
  if (applyGate() === "closed") {
    return;
  }

  clockReady = syncClock().then(applyGate);

  const tick = setInterval(() => {
    if (clockSettled && applyGate() === "open") {
      clearInterval(tick);
    }
  }, 1000);

  // Background tabs get their timers throttled; catch up the moment one is shown.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) applyGate();
  });
}

window.onload = function () {
  init();
};
